import { action, computed, makeObservable, observable } from 'mobx';
import type { ObservableMap } from 'mobx';

import { isObjectEmpty } from '../../../shared/utils';
import type { TAlertRule } from '../../types';

import type { AlertRule } from './AlertRule.entity';

const staticRequiredFields: (keyof TAlertRule)[] = ['riskLevel', 'sourceType', 'sourceOption', 'unitId'];

const diffRequiredFields: (keyof TAlertRule)[] = ['diffInterval', 'diffValue'];
const corridorFollowingRequiredFields: (keyof TAlertRule)[] = ['minValue'];
const corridorFollowingFields: (keyof TAlertRule)[] = ['corridorFollowing', ...corridorFollowingRequiredFields];
const depthRangeFields: (keyof TAlertRule)[] = ['depthFrom', 'depthTo'];
const holeDepthRangeFields: (keyof TAlertRule)[] = ['holeDepthFrom', 'holeDepthTo'];
const requiredFieldErrorKey = 'alertsManagerWidget:errorMessages.requiredField';
const depthRangeErrorKey = 'alertsManagerWidget:errorMessages.depthFromMustBeLessThanDepthTo';

type TErrorsObject = Partial<Record<keyof TAlertRule, string[]>>;

export class AlertRulesValidationService {
  readonly _errors = observable.map<string, TErrorsObject>();

  constructor() {
    makeObservable(this);
  }

  private addError(field: keyof TAlertRule, item: AlertRule, errorKey: string): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    const errorsSet = new Set(errorObject[field] ?? []);

    errorsSet.add(errorKey);

    this._errors.set(item.entityId, {
      ...errorObject,
      [field]: Array.from(errorsSet),
    });
  }

  private removeError(field: keyof TAlertRule, item: AlertRule, errorKey: string): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    const errorsSet = new Set(errorObject[field] ?? []);

    errorsSet.delete(errorKey);

    this._errors.set(item.entityId, {
      ...errorObject,
      [field]: Array.from(errorsSet),
    });
  }

  private removeErrors(field: keyof TAlertRule, item: AlertRule): void {
    const errorObject = this._errors.get(item.entityId) ?? {};

    this._errors.set(item.entityId, {
      ...errorObject,
      [field]: [],
    });
  }

  @computed
  get errors(): ObservableMap<string, TErrorsObject> {
    return this._errors;
  }

  private isValueFilled(value: unknown): boolean {
    return value != null && value !== '';
  }

  private getRelatedFields(field: keyof TAlertRule): (keyof TAlertRule)[] {
    if (field === 'diffInterval' || field === 'diffValue') {
      return diffRequiredFields;
    }

    if (field === 'depthFrom' || field === 'depthTo') {
      return depthRangeFields;
    }

    if (field === 'holeDepthFrom' || field === 'holeDepthTo') {
      return holeDepthRangeFields;
    }

    if (corridorFollowingFields.includes(field)) {
      return corridorFollowingRequiredFields;
    }

    return [field];
  }

  private isFieldRequired(field: keyof TAlertRule, item: AlertRule): boolean {
    if (staticRequiredFields.includes(field)) {
      return true;
    }

    if (field === 'diffInterval') {
      return this.isValueFilled(item.data.diffValue);
    }

    if (field === 'diffValue') {
      return this.isValueFilled(item.data.diffInterval);
    }

    if (field === 'minValue') {
      return item.data.corridorFollowing === true;
    }

    return false;
  }

  private checkFieldRequired(field: keyof TAlertRule, item: AlertRule): void {
    const errorObject = this._errors.get(item.entityId);
    const isRequired = this.isFieldRequired(field, item);

    if (this.isValueFilled(item.data[field])) {
      if (!errorObject || isObjectEmpty(errorObject)) {
        return;
      }

      this.removeError(field, item, requiredFieldErrorKey);
      return;
    }

    if (isRequired) {
      this.addError(field, item, requiredFieldErrorKey);
      return;
    }

    this.removeError(field, item, requiredFieldErrorKey);
  }

  private checkRange(item: AlertRule, fromField: keyof TAlertRule, toField: keyof TAlertRule): void {
    const fromValue = item.data[fromField];
    const toValue = item.data[toField];

    if (!this.isValueFilled(fromValue) || !this.isValueFilled(toValue)) {
      this.removeError(fromField, item, depthRangeErrorKey);
      this.removeError(toField, item, depthRangeErrorKey);
      return;
    }

    if (Number(fromValue) < Number(toValue)) {
      this.removeError(fromField, item, depthRangeErrorKey);
      this.removeError(toField, item, depthRangeErrorKey);
      return;
    }

    this.addError(fromField, item, depthRangeErrorKey);
    this.addError(toField, item, depthRangeErrorKey);
  }

  @action.bound
  validateAlertRuleField(field: keyof TAlertRule, item: AlertRule): boolean {
    const fieldsToValidate = this.getRelatedFields(field);

    for (const fieldName of fieldsToValidate) {
      this.removeErrors(fieldName, item);
      this.checkFieldRequired(fieldName, item);
    }

    if (fieldsToValidate.some((fieldName) => depthRangeFields.includes(fieldName))) {
      this.checkRange(item, 'depthFrom', 'depthTo');
    }

    if (fieldsToValidate.some((fieldName) => holeDepthRangeFields.includes(fieldName))) {
      this.checkRange(item, 'holeDepthFrom', 'holeDepthTo');
    }

    return fieldsToValidate.some((fieldName) => !!this._errors.get(item.entityId)?.[fieldName]?.length);
  }

  validateAlertRuleItem(item: AlertRule): boolean {
    let hasErrors = false;
    const fieldsToValidate = [
      ...new Set([
        ...staticRequiredFields,
        ...diffRequiredFields,
        ...depthRangeFields,
        ...holeDepthRangeFields,
        ...corridorFollowingFields,
      ]),
    ];

    for (const field of fieldsToValidate) {
      const hasFieldErrors = this.validateAlertRuleField(field, item);
      if (hasFieldErrors && !hasErrors) {
        hasErrors = hasFieldErrors;
      }
    }

    return hasErrors;
  }

  validateAlertRules(items: AlertRule[]): boolean {
    let hasErrors = false;

    for (const item of items) {
      const hasItemErrors = this.validateAlertRuleItem(item);
      if (hasItemErrors && !hasErrors) {
        hasErrors = hasItemErrors;
      }
    }

    return hasErrors;
  }
}
